import React from 'react';
import Icon from '../../../components/AppIcon';

const ProcessExplanation = () => {
  const processSteps = [
    {
      step: '01',
      title: 'Initial Consultation',
      duration: '60 minutes',
      description: 'Deep-dive discussion about your current AI landscape, challenges, and strategic objectives. We assess your organizational readiness and identify key opportunities.',
      deliverables: [
        'Current state assessment',
        'Opportunity identification',
        'Initial roadmap outline',
        'Resource requirement analysis'
      ],
      icon: 'MessageSquare'
    },
    {
      step: '02',
      title: 'Strategic Planning',
      duration: '2-3 weeks',
      description: 'Comprehensive analysis and custom strategy development based on your specific needs, industry requirements, and organizational constraints.',
      deliverables: [
        'Detailed transformation strategy',
        'Technology stack recommendations',
        'Implementation timeline',
        'Risk mitigation plan'
      ],
      icon: 'Map'
    },
    {
      step: '03',
      title: 'Implementation Support',
      duration: '3-12 months',
      description: 'Hands-on guidance through the implementation process, from initial setup to full deployment, ensuring smooth transition and optimal results.',
      deliverables: [
        'Technical implementation guidance',
        'Team training and enablement',
        'Performance monitoring setup',
        'Ongoing optimization support'
      ],
      icon: 'Settings'
    },
    {
      step: '04',
      title: 'Success & Scale',
      duration: 'Ongoing',
      description: 'Continuous support to ensure long-term success, scaling capabilities, and maintaining competitive advantage through AI innovation.',
      deliverables: [
        'Performance optimization',
        'Scaling strategy development',
        'Advanced capability building',
        'Innovation roadmap updates'
      ],
      icon: 'TrendingUp'
    }
  ];

  const faqs = [
    {
      question: 'How long does the typical engagement take?',
      answer: 'Most organizations see initial results within 30-60 days, with full transformation typically completed in 3-6 months. The timeline depends on your current infrastructure, team readiness, and scope of implementation.'
    },
    {
      question: 'What if we\'re already using proprietary AI solutions?',
      answer: 'We specialize in migration strategies that minimize disruption while maximizing value. Our approach includes gradual transition plans, data migration support, and parallel system operation to ensure business continuity.'
    },
    {
      question: 'Do you provide ongoing support after implementation?',
      answer: 'Yes, we offer comprehensive post-implementation support including performance monitoring, optimization recommendations, team training, and strategic guidance for scaling your AI capabilities.'
    },
    {
      question: 'What makes open-source AI better than proprietary solutions?',
      answer: 'Open-source AI provides complete control, eliminates vendor lock-in, reduces long-term costs, enables customization, and ensures data sovereignty. You own your AI infrastructure and can adapt it to your specific needs without restrictions.'
    },
    {
      question: 'How do you ensure security and compliance?',
      answer: 'We implement enterprise-grade security frameworks, conduct thorough compliance assessments, and provide detailed documentation for audit purposes. Our open-source approach actually enhances security through transparency and community validation.'
    },
    {
      question: 'What kind of ROI can we expect?',
      answer: 'Most clients see 3-5x ROI within the first year through cost savings, efficiency gains, and new revenue opportunities. We provide detailed ROI projections and tracking throughout the engagement.'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Process Steps */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-brand-primary mb-4">
              Our Proven Implementation Process
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              From initial consultation to full-scale implementation, we guide you through every step of your AI transformation journey
            </p>
          </div>

          <div className="space-y-12">
            {processSteps?.map((step, index) => (
              <div key={index} className="relative">
                {/* Connection Line */}
                {index < processSteps?.length - 1 && (
                  <div className="absolute left-8 top-20 w-0.5 h-24 bg-gradient-to-b from-brand-primary to-brand-secondary hidden lg:block" />
                )}

                <div className="flex flex-col lg:flex-row items-start space-y-6 lg:space-y-0 lg:space-x-8">
                  {/* Step Icon & Number */}
                  <div className="flex-shrink-0 flex items-center space-x-4">
                    <div className="w-16 h-16 bg-brand-primary rounded-2xl flex items-center justify-center">
                      <Icon name={step?.icon} size={24} className="text-white" />
                    </div>
                    <div className="text-4xl font-bold text-brand-primary/20">
                      {step?.step}
                    </div>
                  </div>

                  {/* Step Content */}
                  <div className="flex-1 bg-white rounded-2xl shadow-executive p-8">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
                      <div>
                        <h3 className="text-2xl font-bold text-brand-primary mb-2">
                          {step?.title}
                        </h3>
                        <div className="flex items-center text-text-secondary mb-4">
                          <Icon name="Clock" size={16} className="mr-2" />
                          <span className="text-sm">{step?.duration}</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-text-secondary mb-6 leading-relaxed">
                      {step?.description}
                    </p>

                    <div>
                      <h4 className="font-semibold text-brand-primary mb-3">Key Deliverables:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {step?.deliverables?.map((deliverable, idx) => (
                          <div key={idx} className="flex items-center">
                            <Icon name="CheckCircle" size={16} className="text-green-500 mr-3 flex-shrink-0" />
                            <span className="text-sm text-text-primary">{deliverable}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div>
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-brand-primary mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Get answers to common questions about our AI transformation process and approach
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {faqs?.map((faq, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-executive p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-brand-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon name="HelpCircle" size={16} className="text-brand-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-brand-primary mb-3">
                        {faq?.question}
                      </h3>
                      <p className="text-text-secondary leading-relaxed">
                        {faq?.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-brand-primary to-brand-secondary rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Still have questions?
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Our AI transformation experts are here to help. Schedule a free consultation to discuss your specific needs and get personalized answers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-brand-accent hover:bg-brand-accent/90 text-gray-900 font-semibold px-8 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center">
                <Icon name="Calendar" size={16} className="mr-2" />
                Schedule Free Consultation
              </button>
              <button className="border border-white text-white hover:bg-white hover:text-brand-primary font-semibold px-8 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center">
                <Icon name="MessageCircle" size={16} className="mr-2" />
                Chat with Expert
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessExplanation;